import { useState, useCallback, memo } from 'react';
import type { FC, CSSProperties } from 'react';
import classnames from 'classnames';

import { useOverTimeout } from 'app/hooks/useTimeout';
import { getOpponentAccronym } from 'app/utils/match';

import { getThumbnailUrl } from './utils';
import style from './style.scss';

interface Props {
  opponent: PandapiTeam | PandapiPlayer | undefined;
  size?: number;
  className?: string;
  style?: CSSProperties;
  span?: boolean;
  placeholderClassname?: string;
  placeholderSize?: number;
}

const OpponentAvatar: FC<Props> = ({
  opponent,
  size = 20,
  placeholderSize = size,
  className,
  style: css,
  span = false,
  placeholderClassname
}) => {
  const [status, setStatus] = useState<'loading' | 'succeed' | 'failed'>('loading');
  const loadOvertime = useOverTimeout(100);
  const handleSuccess = useCallback(() => {
    setStatus('succeed');
  }, []);
  const handleError = useCallback(() => {
    setStatus('failed');
  }, []);

  if (!opponent) {
    return null;
  }

  const Wrapper = span ? 'span' : 'div';

  const containerClassName = classnames(style.container, className, {
    [style.container_loading]: loadOvertime && status === 'loading',
    [style.container_span]: span
  });
  const thumbnailUrl = opponent.image_url ? getThumbnailUrl(opponent.image_url) : null;

  const isLoaded = thumbnailUrl && status !== 'failed';
  const fontSize = Math.floor(size / 2) - 1;
  const containerStyle: CSSProperties = {
    width: isLoaded ? size : placeholderSize,
    height: isLoaded ? size : placeholderSize,
    fontSize,
    ...css
  };
  const acronym = getOpponentAccronym(opponent);

  return (
    <Wrapper className={containerClassName} style={containerStyle}>
      {isLoaded ? (
        <img
          className={style.image}
          src={thumbnailUrl}
          alt={acronym}
          title={opponent.name}
          onLoad={handleSuccess}
          onError={handleError}
        />
      ) : (
        <Wrapper className={classnames(style.avatar, placeholderClassname)} title={opponent.name}>
          {acronym}
        </Wrapper>
      )}
    </Wrapper>
  );
};

OpponentAvatar.displayName = 'OpponentAvatar';

export default memo(OpponentAvatar);
